/** One input per language for a text stored as {"fr": "…", "en": "…"}; French is required. */

import { SUPPORTED_LANGUAGES } from "@agencyos/i18n";
import { TextAreaField, TextField } from "@agencyos/ui-web";
import { useTranslation } from "react-i18next";

export type Texts = Record<string, string>;

export function asTexts(value: unknown): Texts {
  if (!value || typeof value !== "object") return {};
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).filter(
      (entry): entry is [string, string] => typeof entry[1] === "string",
    ),
  );
}

/** The text in the reader's language, else French, else any. */
export function localized(value: unknown, language: string): string {
  const texts = asTexts(value);
  return texts[language] ?? texts.fr ?? Object.values(texts)[0] ?? "";
}

export function TranslatedFields({
  label,
  value,
  onChange,
  multiline = false,
  required = true,
  disabled = false,
  error,
}: {
  label: string;
  value: Texts;
  onChange: (value: Texts) => void;
  multiline?: boolean;
  required?: boolean;
  disabled?: boolean;
  error?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="translated-fields">
      {SUPPORTED_LANGUAGES.map((language, index) => {
        const optional = language !== "fr" || !required;
        const props = {
          label: `${label} (${t(`catalog.language.${language}`)}${optional ? `, ${t("common.optional")}` : ""})`,
          value: value[language] ?? "",
          disabled,
          lang: language,
          error: index === 0 ? error : undefined,
        };
        const set = (text: string) => onChange({ ...value, [language]: text });
        return multiline ? (
          <TextAreaField key={language} {...props} rows={3} onChange={(e) => set(e.target.value)} />
        ) : (
          <TextField key={language} {...props} onChange={(e) => set(e.target.value)} />
        );
      })}
    </div>
  );
}
